import { Row } from '@tanstack/react-table'
import { ControlSheetRow, ControlSheetTrip } from '../types/types'

/**
 * Sorts control sheet rows by trip number, handling four categories of rows:
 *
 *   1. "Early inbound-only" — inbound trip with no outbound, where the inbound
 *      trip number is below the lowest outbound's corresponding inbound. These
 *      represent trips arriving before any departures and sort to the top.
 *
 *   2. "Both" — rows with both an outbound and inbound trip. Sorted by outbound
 *      trip number.
 *
 *   3. "Outbound-only" — rows with an outbound trip but no inbound. Sorted by
 *      outbound trip number. When two rows share the same outbound number, the
 *      outbound-only row sorts before the row that also has an inbound.
 *
 *   4. "Late inbound-only" — inbound trip with no outbound, where the inbound
 *      trip number is at or above the threshold. These represent trips arriving
 *      after the main schedule and sort among themselves by inbound trip number.
 *
 * For inbound-only rows, `oppositeTrip` (set by `identifyOppositeTrips`) is
 * used as a proxy outbound number so they interleave correctly with outbound rows.
 *
 * @param inboundTripNumForLowestOutbound - The inbound trip number on the row
 *   that has the lowest outbound trip. Acts as the threshold dividing "early"
 *   from "late" inbound-only rows.
 */
const controlSheetRowSorter = (
    aOutbound?: ControlSheetTrip,
    aInbound?: ControlSheetTrip,
    bOutbound?: ControlSheetTrip,
    bInbound?: ControlSheetTrip,
    inboundTripNumForLowestOutbound?: number
): number => {
    const isValid = (value?: string | null): boolean => {
        return value != null && value !== '-'
    }

    // Parse all trip numbers to integers for numeric comparison
    const aOutboundNum = isValid(aOutbound?.trip) ? parseInt(aOutbound!.trip, 10) : null
    const bOutboundNum = isValid(bOutbound?.trip) ? parseInt(bOutbound!.trip, 10) : null
    const aInboundNum = isValid(aInbound?.trip) ? parseInt(aInbound!.trip, 10) : null
    const bInboundNum = isValid(bInbound?.trip) ? parseInt(bInbound!.trip, 10) : null

    // Opposite trip numbers allow inbound-only rows to sort relative to outbound rows
    const aInboundOpposite = isValid(aInbound?.oppositeTrip) ? parseInt(aInbound!.oppositeTrip!, 10) : null
    const bInboundOpposite = isValid(bInbound?.oppositeTrip) ? parseInt(bInbound!.oppositeTrip!, 10) : null

    // --- Category 4: Late inbound-only rows (inbound >= threshold, no outbound) ---
    // These are end-of-day arrivals with no corresponding departure
    const aIsLateInboundOnly =
        !aOutboundNum &&
        aInboundNum &&
        inboundTripNumForLowestOutbound &&
        aInboundNum >= inboundTripNumForLowestOutbound
    const bIsLateInboundOnly =
        !bOutboundNum &&
        bInboundNum &&
        inboundTripNumForLowestOutbound &&
        bInboundNum >= inboundTripNumForLowestOutbound

    if (aIsLateInboundOnly && bIsLateInboundOnly) {
        return aInboundNum! - bInboundNum!
    }

    // --- Category 1: Early inbound-only rows (inbound < threshold, no outbound) ---
    // These are early arrivals before departures begin; always sort to the top
    const aIsEarlyInboundOnly =
        !aOutboundNum && aInboundNum && inboundTripNumForLowestOutbound && aInboundNum < inboundTripNumForLowestOutbound
    const bIsEarlyInboundOnly =
        !bOutboundNum && bInboundNum && inboundTripNumForLowestOutbound && bInboundNum < inboundTripNumForLowestOutbound

    if (aIsEarlyInboundOnly && !bIsEarlyInboundOnly) return -1
    if (!aIsEarlyInboundOnly && bIsEarlyInboundOnly) return 1
    if (aIsEarlyInboundOnly && bIsEarlyInboundOnly) {
        return aInboundNum! - bInboundNum!
    }

    // --- Category 2: Rows with both outbound and inbound ---
    const aHasBoth = aOutboundNum && aInboundNum
    const bHasBoth = bOutboundNum && bInboundNum

    // General case: compare by outbound number, falling back to oppositeTrip
    // for inbound-only rows so they interleave at the right position
    if ((aOutboundNum || aInboundOpposite) && (bOutboundNum || bInboundOpposite)) {
        return (aOutboundNum ?? aInboundOpposite)! - (bOutboundNum ?? bInboundOpposite)!
    }

    if (aHasBoth && bHasBoth) {
        return aOutboundNum! - bOutboundNum!
    }

    // When comparing a "both" row against an inbound-only row with an opposite,
    // the inbound-only row sorts first on tie (it represents the arriving half)
    if (aHasBoth && bInboundOpposite) {
        if (aOutboundNum === bInboundOpposite) return 1
        return aOutboundNum! - bInboundOpposite!
    }

    if (bHasBoth && aInboundOpposite) {
        if (aInboundOpposite === bOutboundNum) return -1
        return aInboundOpposite - bOutboundNum!
    }

    // --- Category 3: Outbound-only rows (outbound trip, no inbound) ---
    const aIsOutboundOnly = aOutboundNum && !aInboundNum
    const bIsOutboundOnly = bOutboundNum && !bInboundNum

    // Mixed: "both" vs "outbound-only" — on same outbound number,
    // outbound-only sorts first (departure without a return listed)
    if (aHasBoth && bIsOutboundOnly) {
        if (aOutboundNum === bOutboundNum) return 1
        return aOutboundNum! - bOutboundNum!
    }
    if (aIsOutboundOnly && bHasBoth) {
        if (aOutboundNum === bOutboundNum) return -1
        return aOutboundNum! - bOutboundNum!
    }

    if (aIsOutboundOnly && bIsOutboundOnly) {
        return aOutboundNum! - bOutboundNum!
    }

    // --- Edge cases: mixed categories that don't fit the rules above ---

    // Rows with outbound info sort before rows without any outbound
    if ((aHasBoth || aIsOutboundOnly) && !(bHasBoth || bIsOutboundOnly)) return -1

    // Late inbound-only vs outbound-only: use oppositeTrip as proxy
    if (aIsLateInboundOnly && bIsOutboundOnly) {
        if (aInboundOpposite) {
            return aInboundOpposite - bOutboundNum!
        }
    }

    // Late inbound-only vs rows with both: fall back to comparing inbound numbers
    if (aIsLateInboundOnly && bHasBoth) {
        return aInboundNum! - bInboundNum!
    }
    if (bIsLateInboundOnly && aHasBoth) {
        return aInboundNum! - bInboundNum!
    }

    return 0
}

/**
 * Curried comparator for TanStack Table `Row` objects. Wraps `controlSheetRowSorter`.
 */
export const tripSorter =
    (inboundTripNumForLowestOutbound: number) => (a: Row<ControlSheetRow>, b: Row<ControlSheetRow>) =>
        tripRowSorter(inboundTripNumForLowestOutbound)(a.original, b.original)

/**
 * Curried comparator for raw `ControlSheetRow` objects. Wraps `controlSheetRowSorter`.
 */
export const tripRowSorter =
    (inboundTripNumForLowestOutbound: number) =>
    (a: ControlSheetRow, b: ControlSheetRow): number => {
        return controlSheetRowSorter(a.outbound, a.inbound, b.outbound, b.inbound, inboundTripNumForLowestOutbound)
    }
